import React from 'react';

import {assertAndContinue} from 'Asserts';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import Section from 'ui-library/menus/Section';
import SectionHeader from 'ui-library/menus/SectionHeader';
import runModal from 'ui-library/runModal';
import AddToWorklistModal from 'worklists/AddToWorklistModal';
import {WorklistTypeToModelTypeConstant} from 'worklists/WorklistConstants';
import {
  apiIdToWorklistType,
  areIdsValidForWorklist,
  getWorklistType,
  idsToTooltipTitle,
} from 'worklists/WorklistUtil';

export interface WorklistColumn {
  name: string;
  itemIds: Array<string>;
}

export interface AddColumnToWorklistMenuProps {
  columns: Array<WorklistColumn>;
}

function columnToIcon({itemIds}: WorklistColumn): BenchlingIconName {
  if (itemIds.length) {
    const worklistType = apiIdToWorklistType(itemIds[0]);
    if (worklistType != null) {
      return WorklistTypeToModelTypeConstant[worklistType] as BenchlingIconName;
    }
  }
  // Default icon
  return ModelTypeConstants.SAMPLE;
}

function handleSelectColumn({itemIds}: WorklistColumn): void {
  assertAndContinue(areIdsValidForWorklist(itemIds), 'Column items are not valid for worklist', {itemIds});
  assertAndContinue(itemIds.length === new Set(itemIds).size, 'Column items have duplicates', {itemIds});
  runModal(AddToWorklistModal, {
    ids: itemIds,
    worklistType: getWorklistType(itemIds),
    refreshOptions: {refreshRecentWorklists: true},
  });
}

function columnToOption(column: WorklistColumn, columnIndex: number): JSX.Element {
  assertAndContinue(column.itemIds.length > 0, 'Column has no items', {name: column.name});
  const idsValid = areIdsValidForWorklist(column.itemIds);
  // Only show tooltip if ids are invalid
  const tooltipTitle = idsValid ? undefined : idsToTooltipTitle(column.itemIds);
  return (
    <Option
      key={`${columnIndex}-${column.name}`}
      icon={columnToIcon(column)}
      isDisabled={!idsValid}
      onSelect={handleSelectColumn}
      shouldEllipsize={true}
      tooltipTitle={tooltipTitle}
      value={column}
    >
      {column.name}
    </Option>
  );
}

/**
 * Render menu to add items in columns to worklist
 *
 * The reason this is a normal function, instead of React component, is that Dropdown doesn't support
 * components wrapping Menu. It expects its child component to be a Menu so that it can clone the Menu.
 */
export default function getAddColumnToWorklistMenu(props: AddColumnToWorklistMenuProps): JSX.Element {
  return (
    <Menu height={Menu.HEIGHTS.MEDIUM} width={Menu.WIDTHS.MEDIUM}>
      <Section>
        <SectionHeader>Column</SectionHeader>
        {props.columns.length ? (
          props.columns.map((column, columnIndex) => columnToOption(column, columnIndex))
        ) : (
          <Option isDisabled={true}>No columns for worklists found</Option>
        )}
      </Section>
    </Menu>
  );
}
